import { Skeleton } from '@neo/primitives/skeleton'
import { QueryError } from 'v2/api-console/shared/components/query-error'
import type { FlowVersion } from '../../types'
import { FlowVersionRow } from './flow-version-row'
import { groupVersionsByDate } from './version-utils'

interface FlowVersionListProps {
  versions: FlowVersion[]
  createdByName?: string
  selectedVersionId: number | null
  isLoading: boolean
  isError: boolean
  isRetrying?: boolean
  onRetry: () => unknown
  onViewVersion: (version: FlowVersion) => void
  onSwitch: (version: FlowVersion) => void
  onEditSummary: (version: FlowVersion) => void
}

export const FlowVersionList = ({
  versions,
  createdByName,
  selectedVersionId,
  isLoading,
  isError,
  isRetrying,
  onRetry,
  onViewVersion,
  onSwitch,
  onEditSummary,
}: FlowVersionListProps) => {
  const groups = groupVersionsByDate(versions)

  return (
    <div data-testid="version-history-list">
      <div className="border-b border-st2 px-4 py-3">
        <p className="text-p-500 text-primary">Version history</p>
        {createdByName && (
          <p className="text-xs-400 text-secondary">Flow created by {createdByName}</p>
        )}
      </div>

      <div className="max-h-[60vh] overflow-y-auto py-1">
        {isLoading && !isError && (
          <section aria-label="Loading versions" aria-busy="true" className="space-y-4 px-4 py-3">
            {['first', 'second', 'third'].map((row) => (
              <div key={row} className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-40" />
              </div>
            ))}
          </section>
        )}
        {isError && (
          <div className="p-3">
            <QueryError
              title="Couldn't load versions."
              onRetry={onRetry}
              isRetrying={isRetrying}
              testId="button-retry-flow-versions"
            />
          </div>
        )}
        {!isLoading && !isError && versions.length === 0 && (
          <p className="px-4 py-6 text-center text-s-400 text-secondary">No versions yet.</p>
        )}
        {groups.map((group) => (
          <div key={group.label}>
            <p className="px-4 pt-3 pb-1 text-xs-500 text-secondary">{group.label}</p>
            {group.versions.map((version) => (
              <FlowVersionRow
                key={version.id}
                version={version}
                isSelected={selectedVersionId === version.id}
                onViewVersion={() => onViewVersion(version)}
                onSwitch={() => onSwitch(version)}
                onEditSummary={() => onEditSummary(version)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
